import { useState } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import { t } from "ttag";

import { usePortalUserStore } from "@/app/stores";
import {
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
} from "@/features/billing/hooks";
import { LogoLoader } from "@/shared/components";
import type { ModalProps } from "@/shared/models/legacy-types";
import type { CheckedState } from "@/shared/ui";
import {
  Button,
  Checkbox,
  Dialog,
  DocumentAddIcon,
  errorToast,
  useToastStore,
} from "@/shared/ui";

export const UpdatePaymentMethodModal = ({ show, onClose }: ModalProps) => {
  const portalUser = usePortalUserStore((state) => state.portalUser);

  const pushToast = useToastStore((state) => state.pushToast);

  const [hasConsent, setHasConsent] = useState<CheckedState>(false);

  const createPaymentMethodMutation = useCreatePaymentMethod();
  const updatePaymentMethodMutation = useUpdatePaymentMethod();

  const isPending =
    createPaymentMethodMutation.isPending ||
    updatePaymentMethodMutation.isPending;

  const handleUpdatePaymentMethod = () => {
    createPaymentMethodMutation.mutate(
      {
        email: portalUser?.email,
        name: portalUser?.name,
      },
      {
        onSuccess: (paymentMethodId) => {
          updatePaymentMethodMutation.mutate(paymentMethodId, {
            onSuccess: () => {
              onClose();
              void pushToast({
                type: "success",
                title: t`Card info updated`,
                message: t`Your card information has been successfully updated`,
              });
            },
          });
        },
        onError: (error) => {
          errorToast(
            error.message ||
              t`Error updating payment method, try again later or use a different card.`,
          );
        },
      },
    );
  };

  const handleClose = () => {
    setHasConsent(false);
    onClose();
  };

  return (
    <Dialog.Root open={show} onOpenChange={handleClose}>
      <Dialog.Content>
        <Dialog.Header icon={DocumentAddIcon}>
          <Dialog.Title>{t`Edit your payment method`}</Dialog.Title>

          <Dialog.Description>
            {t`Update the details for your preferred payment method.`}
          </Dialog.Description>
        </Dialog.Header>

        <div className="flex flex-col gap-2">
          <div className="flex w-full flex-col gap-1">
            <label className="text-sm font-medium text-neutral-950">
              {t`Card number *`}
            </label>
            <CardElement className="w-full rounded-lg border border-neutral-900 p-3" />
          </div>

          <Checkbox.Item checked={hasConsent} onCheckedChange={setHasConsent}>
            <Checkbox.Label>
              {t`I authorize the use of this credit card for subscriptions, medications and lab charges`}
            </Checkbox.Label>
          </Checkbox.Item>
        </div>

        <Dialog.Footer>
          <Button size="lg" variant="secondary" onClick={handleClose}>
            {t`Cancel`}
          </Button>

          <Button
            size="lg"
            onClick={handleUpdatePaymentMethod}
            disabled={isPending || !hasConsent}
          >
            {t`Save changes`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
